import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { LocalConfig, transformParameters } from 'utils';

import { NotificationManager, PaymentV2Api, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, Loader, Select } from '@atlas/ui';

import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

const ACCOUNT_NUMBER_MIN_LENGTH = 10;
const ACCOUNT_NUMBER_MAX_LENGTH = 18;
const DOCUMENT_NUMBER_MIN_LENGTH = 12;
const DOCUMENT_NUMBER_MAX_LENGTH = 13;

const validateNumber = (value, min, max) => {
  if (!value) return { message: 'required' };

  if (value.length < min) {
    return { message: 'min_length', params: { ruleOption: min } };
  } else if (value.length > max) {
    return { message: 'max_length', params: { ruleOption: max } };
  } else {
    return null;
  }
};

const showErrorPopup = () => {
  NotificationManager.display({
    data: {
      errorType: true,
      headerTitle: 'withdrawal',
      text: 'something_went_wrong_contact_support',
      title: 'failed_sad_emoticon',
    },
    type: 'withdrawalResultPopup',
  });
};

const WithdrawalTonderPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;

  const [loading, setLoading] = useState(true);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [fieldsData, setFieldsData] = useState({});
  const [accountError, setAccountError] = useState(null);
  const [documentError, setDocumentError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectId = LocalConfig.get('projectId');

  const [bankCodeInput, setBankCodeInput] = useState('');
  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [documentNumberInput, setDocumentNumberInput] = useState('');

  const { amount, country, currency, documentNumber, documentType } =
    parseWithdrawalQuery(redirectUrl);

  const isShowDocumentNumberInput = !documentNumber && !!documentType;

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const requestUrl = useMemo(
    () =>
      `payment/v2/payments/tonder/parameters?project-id=${projectId}&country=MX&param-names=BANK_CODE`,
    [projectId],
  );

  useEffect(() => {
    const getFieldsData = prepareRequest({
      method: 'GET',
      url: requestUrl,
    });

    setLoading(true);
    getFieldsData()
      .then(response => {
        const parsedData = transformParameters(response.paymentParametersList);
        setFieldsData(parsedData);
      })
      .catch(() => {
        showErrorPopup();
        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleClose, requestUrl]);

  const changeAccountNumber = useCallback(({ value }) => {
    const formattedValue = value.split('.')[1];
    const error = validateNumber(
      formattedValue,
      ACCOUNT_NUMBER_MIN_LENGTH,
      ACCOUNT_NUMBER_MAX_LENGTH,
    );

    setAccountError(error);
    setAccountNumberInput(formattedValue);
  }, []);

  const changeDocumentNumber = useCallback(event => {
    const value = event.target.value.replace(/[^a-zA-Z0-9]/g, '');

    const error = validateNumber(value, DOCUMENT_NUMBER_MIN_LENGTH, DOCUMENT_NUMBER_MAX_LENGTH);

    setDocumentError(error);
    setDocumentNumberInput(value);
  }, []);

  const handleClickButton = useCallback(async () => {
    let additionalParameters = {
      accountNumber: accountNumberInput,
      institution: bankCodeInput,
      publicPaymentId: publicPaymentId,
    };

    if (documentType) {
      additionalParameters = {
        ...additionalParameters,
        documentNumber: isShowDocumentNumberInput ? documentNumberInput : documentNumber,
        documentType,
      };
    }

    const requestData = {
      additionalParameters,
      amount: amount,
      currency: currency,
      pmId: pmId,
    };

    setIsSubmitting(true);

    try {
      const response = await PaymentV2Api.createAsyncWithdrawalV2(requestData);
      NotificationManager.display({
        data: {
          amount,
          currency,
          publicPaymentId: response?.publicPaymentId,
          paymentMethod,
        },
        message: 'successfulWithdrawal',
        type: 'successfulWithdrawal',
      });
    } catch (error) {
      showErrorPopup();
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    accountNumberInput,
    amount,
    bankCodeInput,
    currency,
    documentNumber,
    documentNumberInput,
    documentType,
    handleClose,
    isShowDocumentNumberInput,
    paymentMethod,
    pmId,
    publicPaymentId,
  ]);

  return (
    <DefaultPopup
      className={cx('payment-popup', 'full-height')}
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payment-popup-content">
        <div className="popup-payment-method">
          <PaymentListItem amount={amount} payment={paymentMethod} showAmount />
        </div>

        {loading && <Loader className={cx('form-loader', { 'loader-long': country === 'EC' })} />}

        {!loading && (
          <>
            <div className="payment-popup-block">
              <NumberFormat
                active={isActiveInput === 'withdrawal_account_number'}
                allowNegative={false}
                allowedDecimalSeparators={[]}
                className="popup-input"
                customInput={Input}
                decimalSeparator=""
                error={accountError}
                inputMode="decimal"
                label="withdrawal_account_number"
                onBlur={() => setIsActiveInput(false)}
                onFocus={() => setIsActiveInput('withdrawal_account_number')}
                onValueChange={changeAccountNumber}
                type="tel"
                value={accountNumberInput}
              />
              {accountError && (
                <div className="help-text-container">
                  <Label message={accountError.message} params={accountError.params} />
                </div>
              )}
            </div>
            {fieldsData['BANK_CODE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_bank_code'}
                className="payment-popup-select"
                label="withdrawal_bank_code"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setBankCodeInput}
                onFocus={() => setIsActiveSelect('withdrawal_bank_code')}
                options={fieldsData['BANK_CODE']}
              />
            )}
            {isShowDocumentNumberInput && (
              <div className="payment-popup-block">
                <Input
                  active={isActiveInput === 'rfc_client_number'}
                  className="popup-input"
                  error={documentError}
                  label="rfc_client_number"
                  maxLength={DOCUMENT_NUMBER_MAX_LENGTH}
                  minLength={DOCUMENT_NUMBER_MIN_LENGTH}
                  onBlur={() => setIsActiveInput(false)}
                  onChange={changeDocumentNumber}
                  onFocus={() => setIsActiveInput('rfc_client_number')}
                  value={documentNumberInput}
                />
                {documentError && (
                  <div className="help-text-container">
                    <Label message={documentError.message} params={documentError.params} />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {!loading && (
        <div className="popup__footer">
          <Button
            className="primary big block"
            disabled={!accountNumberInput || isSubmitting || accountError || documentError}
            error={accountError || documentError}
            label="withdrawal"
            loading={isSubmitting}
            onClick={handleClickButton}
          />
        </div>
      )}
    </DefaultPopup>
  );
};

WithdrawalTonderPopup.propTypes = {
  data: PropTypes.shape({
    paymentMethod: PropTypes.string,
    pmId: PropTypes.string,
    publicPaymentId: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default WithdrawalTonderPopup;
